import { Typography } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { ChevronRight } from '@emotion-icons/fa-solid/ChevronRight'
import { MapMarkerAlt } from '@emotion-icons/fa-solid/MapMarkerAlt'
import { Colors } from 'src/components/theme'
import { AMLocation, AMOwnershipParcelComplete } from 'src/common-services'
import { FlexColumn, FlexRow } from 'src/components/flex-containers'
import styled from '@emotion/styled'
import { USAIcon } from 'src/components/icons/usa'
import { MapSquareIcon } from 'src/components/icons/map-square'
import { LocationLogo } from 'src/components/insights/location-visits/stats/location-logo'
import { MobilityResults, NearbyResults } from 'src/server/data/alphamap/search'
import { SelectedEntity } from 'src/v2/types/core'
import { ParcelOverlay } from 'src/views/map-explorer/sidebar/related-results/parcel-overlay'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { isFeatureEnabled } from 'src/utils/feature-flag'
import { DedupeResults } from 'src/views/map-explorer/sidebar/related-results/types'
import { ColoredItem, ColoredItemProps } from 'src/components/colored-item'
import { NearbyPlacesOverlay } from 'src/views/map-explorer/sidebar/related-results/nearby-places-overlay'
import { COMMANDAI_IDS_MAP } from 'src/constants/command-ai-ids'
import { formatString } from 'src/utils/format-string'
import { GenericLoader } from 'src/v2/components/skeleton-loaders'

const getFilteredResults = (results: MobilityResults | NearbyResults | undefined, selectedEntity?: SelectedEntity) => {
  if (!results) {
    return undefined
  }
  const locations =
    selectedEntity?.type === 'SELECTED_LOCATION'
      ? results.locations.filter((l) => l.id !== selectedEntity.data['am-loc'].id)
      : results.locations
  const shoppingCenters =
    selectedEntity?.type === 'SELECTED_SHOPPING_CENTER'
      ? results.shoppingCenters.filter((l) => l.id !== selectedEntity.data['am-loc'].id)
      : results.shoppingCenters
  return {
    locations,
    shoppingCenters,
    all: [...shoppingCenters, ...locations],
  }
}

const Content = styled.div`
  padding: 12px;
  border-radius: 6px;
  background-color: ${Colors.neutral50};
`

const LevelWrapper = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  margin-left: 12px;

  & > div.item {
    padding-left: 12px;
    padding-bottom: 12px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 1px;
      background: ${Colors.neutral400};
    }
  }

  & > div.item > div.item-container {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      left: -13px;
      top: 50%;
      width: 8px;
      height: 1px;
      background: ${Colors.neutral400};
    }
  }

  & > div.item:last-of-type {
    position: relative;
    padding-bottom: 0;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 50%;
      width: 1px;
      background: ${Colors.neutral400};
    }
  }
`

type ItemProps = {
  color?: ColoredItemProps['color']
  children: React.ReactNode
  newLevel?: React.ReactNode
  highlight?: ColoredItemProps['highlight']
  onMouseEnter?: ColoredItemProps['onMouseEnter']
  onMouseLeave?: ColoredItemProps['onMouseLeave']
  onClick?: ColoredItemProps['onClick']
}
const Item: FC<ItemProps> = ({ children, color, highlight, onMouseEnter, onMouseLeave, onClick, newLevel }) => {
  return (
    <div className="item">
      <ColoredItem
        className="item-container"
        color={color}
        highlight={highlight}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}>
        {children}
      </ColoredItem>
      {newLevel}
    </div>
  )
}

type LocationBreadcrumbProps = {
  parcel: AMOwnershipParcelComplete
}

const LocationBreadcrumb: FC<LocationBreadcrumbProps> = ({ parcel }) => {
  return (
    <FlexRow gap={4} align="center">
      <USAIcon size={18} color={Colors.neutral700} />
      <Typography variant="textmdm" color={Colors.neutral700}>
        {parcel.state}
      </Typography>
      <ChevronRight size={12} color={Colors.neutral300} />
      <Typography variant="textmdm" color={Colors.neutral700}>
        {formatString(parcel.city, 'name')}
      </Typography>
      <ChevronRight size={12} color={Colors.neutral300} />
      <Typography variant="textmdm" color={Colors.neutral700}>
        {parcel.zip}
      </Typography>
    </FlexRow>
  )
}

type ParcelContentProps = {
  parcel: AMOwnershipParcelComplete
  placesWithinParcelResults?: DedupeResults
}
const ParcelContent: FC<ParcelContentProps> = ({ parcel, placesWithinParcelResults }) => {
  const subtitle = useMemo(() => {
    if (!placesWithinParcelResults?.all.length) {
      return 'No Places Inside Parcel'
    }
    return `${placesWithinParcelResults?.all.length} Place${placesWithinParcelResults?.all.length === 1 ? '' : 's'} Inside Parcel`
  }, [placesWithinParcelResults])

  return (
    <FlexRow gap={4}>
      <MapSquareIcon size={12} color={Colors.primaryGreen700} style={{ marginTop: 3 }} />
      <FlexColumn style={{ overflow: 'hidden', flex: 1 }}>
        <FlexRow gap={4}>
          <Typography variant="textmd" color={Colors.neutral700}>
            Parcel:
          </Typography>
          <Typography variant="textmdm" color={Colors.neutral700} noWrap>
            {formatString(parcel.address, 'address')}
          </Typography>
        </FlexRow>
        <Typography variant="textmdm" color={Colors.neutral400}>
          {subtitle}
        </Typography>
      </FlexColumn>
    </FlexRow>
  )
}

type LocationContentProps = {
  location: AMLocation
}
const LocationContent: FC<LocationContentProps> = ({ location }) => {
  return (
    <FlexRow gap={4} align="center">
      <LocationLogo location={location} size={16} />
      <Typography variant="textmd" color={Colors.neutral700} whiteSpace="nowrap">
        Selected Place:
      </Typography>
      <Typography variant="textmdm" color={Colors.neutral700} noWrap>
        {`${location?.name}, ${location?.street}`}
      </Typography>
    </FlexRow>
  )
}

type NearbyPlacesContentProps = {
  nearbyResults?: DedupeResults
}
const NearbyPlacesContent: FC<NearbyPlacesContentProps> = ({ nearbyResults }) => {
  return (
    <FlexRow gap={4} align="center">
      <MapMarkerAlt size={12} color={Colors.primaryBlue500} />
      <Typography variant="textmd" color={Colors.neutral700} id={COMMANDAI_IDS_MAP.mapExplorer['nearby-locations']}>
        Nearby Places:
      </Typography>
      <Typography variant="textmdm" color={Colors.neutral700}>
        {nearbyResults?.all.length ?? 0}
      </Typography>
    </FlexRow>
  )
}

export const SelectedEntityTreeContentSkeleton = () => {
  return (
    <Content>
      <Item color={Colors.neutral700}>
        <GenericLoader height={18} width={160} variant="text" />
      </Item>
      <LevelWrapper>
        <Item
          color={Colors.primaryGreen700}
          newLevel={
            <LevelWrapper>
              <Item color={Colors.primaryBlue500}>
                <GenericLoader height={18} width={160} variant="text" />
              </Item>
            </LevelWrapper>
          }>
          <FlexColumn gap={2}>
            <GenericLoader height={18} width={160} variant="text" />
            <GenericLoader height={14} width={120} variant="text" />
          </FlexColumn>
        </Item>
        <Item color={Colors.primaryBlue500}>
          <GenericLoader height={18} width={160} variant="text" />
        </Item>
      </LevelWrapper>
    </Content>
  )
}

type SelectedEntityTreeContentProps = {
  selectedEntity: SelectedEntity
  parcel: AMOwnershipParcelComplete
  nearbyPlacesResults?: NearbyResults
  placesWithinParcelResults?: MobilityResults
}

export const SelectedEntityTreeContent: FC<SelectedEntityTreeContentProps> = ({
  parcel,
  nearbyPlacesResults,
  placesWithinParcelResults,
  selectedEntity,
}) => {
  const ownerRevealEnabled = isFeatureEnabled('owner-reveal')
  const { selectParcel } = useMapExplorerNavigation()
  const [overlayAnchorElement, setOverlayAnchorElement] = useState<HTMLElement | null>(null)
  const [showParcelOverlay, setShowParcelOverlay] = useState<boolean>(false)
  const [showNearbyPlacesOverlay, setShowNearbyPlacesOverlay] = useState<boolean>(false)

  const location = useMemo(() => {
    if (selectedEntity?.type === 'SELECTED_LOCATION' || selectedEntity?.type === 'SELECTED_SHOPPING_CENTER') {
      return selectedEntity?.data['am-loc']
    }
    return undefined
  }, [selectedEntity])

  const selectedId = useMemo(() => {
    if (selectedEntity?.type === 'SELECTED_LOCATION' || selectedEntity?.type === 'SELECTED_SHOPPING_CENTER') {
      return selectedEntity.data['am-loc'].id
    }
    if (selectedEntity?.type === 'SELECTED_ADDRESS') {
      return selectedEntity.data.place_id
    }

    if (selectedEntity?.type === 'SELECTED_PARCEL') {
      return selectedEntity.data.id
    }

    return undefined
  }, [selectedEntity])

  const nearbyPlaces = useMemo(
    () => getFilteredResults(nearbyPlacesResults, selectedEntity),
    [nearbyPlacesResults, selectedEntity]
  )
  const placesWithinParcel = useMemo(() => getFilteredResults(placesWithinParcelResults), [placesWithinParcelResults])

  const onMouseEnterParcel = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setOverlayAnchorElement(e.currentTarget)
    setShowParcelOverlay(true)
  }

  const onMouseLeaveParcel = () => {
    setOverlayAnchorElement(null)
    setShowParcelOverlay(false)
  }

  const onClickParcel = async () => {
    if (selectedEntity?.type !== 'SELECTED_PARCEL' && ownerRevealEnabled) {
      await selectParcel(parcel.id)
    }
  }

  const onMouseEnterNearbyPlaces = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setOverlayAnchorElement(e.currentTarget)
    setShowNearbyPlacesOverlay(true)
  }

  const onMouseLeaveNearbyPlaces = () => {
    setOverlayAnchorElement(null)
    setShowNearbyPlacesOverlay(false)
  }

  return (
    <div>
      <Content>
        <Item color={Colors.neutral700}>
          <LocationBreadcrumb parcel={parcel} />
        </Item>
        <LevelWrapper>
          <Item
            color={Colors.primaryGreen700}
            highlight={selectedId === parcel.id}
            onMouseEnter={onMouseEnterParcel}
            onMouseLeave={onMouseLeaveParcel}
            onClick={onClickParcel}
            newLevel={
              location && (
                <LevelWrapper>
                  <Item color={Colors.primaryBlue500} highlight={selectedId === location.id}>
                    <LocationContent location={location} />
                  </Item>
                </LevelWrapper>
              )
            }>
            <ParcelContent parcel={parcel} placesWithinParcelResults={placesWithinParcel} />
          </Item>
          <Item
            color={Colors.primaryBlue500}
            onMouseEnter={onMouseEnterNearbyPlaces}
            onMouseLeave={onMouseLeaveNearbyPlaces}>
            <NearbyPlacesContent nearbyResults={nearbyPlaces} />
          </Item>
        </LevelWrapper>
      </Content>
      <ParcelOverlay
        anchorEl={overlayAnchorElement}
        open={showParcelOverlay}
        onClose={() => setShowParcelOverlay(false)}
        parcel={parcel}
        placesWithinParcelResults={placesWithinParcel}
      />
      <NearbyPlacesOverlay
        anchorEl={overlayAnchorElement}
        open={showNearbyPlacesOverlay}
        onClose={() => setShowNearbyPlacesOverlay(false)}
        nearbyResults={nearbyPlaces}
        radius={nearbyPlacesResults?.radius}
      />
    </div>
  )
}
